import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosResponse } from 'axios'
import { orderBy } from 'lodash'

import { DuckDBSchemaType, Tag } from '@types'
import { ApiListResponse } from '../utils/types'

import { TAG_FIELD } from '@/const'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  trimObjectValues,
  updateStoreListItem,
} from '../utils/helpers'

import { useDuckDB } from '@/services/duckdb'

import api from '@/store/api'
import { useRepositoriesStore } from '@/store/repositories/index'

export const TABLE_NAME = 'tags'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [TAG_FIELD.ID]: 'VARCHAR',
  [TAG_FIELD.TAG_NAME]: 'VARCHAR',
  [TAG_FIELD.TAG_VALUE]: 'VARCHAR',
}

export const useTagsStore = defineStore('tags', () => {
  // INIT
  const { pushData, createTable } = useDuckDB()

  const repositoriesStore = useRepositoriesStore()

  const list = ref<Tag[]>([])

  const initFlag = ref(false)
  const trigger = ref(0)

  const loading = ref(true)
  const loadingAction = ref(false)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS

  const getList = computed<Tag[]>(() =>
    orderBy(
      list.value,
      ['tag_name', 'tag_value'].map(
        field => (item: Tag) =>
          item[field as keyof Tag]?.toString().toLowerCase(),
      ),
      ['asc'],
    ),
  )

  // SETTERS

  const setList = async (data: Tag[]) => {
    list.value = data
    await createTable(TABLE_NAME, TABLE_SCHEMA)
    await pushData(TABLE_NAME, data, TABLE_SCHEMA)
    trigger.value++
  }

  const fetch = async (): Promise<Tag[]> => {
    loading.value = true
    try {
      const result = await api.get<ApiListResponse<Tag[]>>('asset_tags', {
        params: {
          repository_id: repositoryId.value,
        },
        signal: abortController.signal,
      })
      const data = result.data.data
      await setList(data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      initFlag.value = true
      loading.value = false
    }
  }

  const store = async (tag: Tag): Promise<Tag> => {
    loadingAction.value = true
    try {
      const trimmedTag = trimObjectValues(tag, ['tag_name', 'tag_value'])
      const result = await api.post<Tag, AxiosResponse<Tag>, Tag>(
        'asset_tags',
        { ...trimmedTag, repository_id: repositoryId.value },
      )
      const data = result.data
      addStoreListItem<Tag>(list.value, data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (tag: Tag): Promise<Tag | undefined> => {
    loadingAction.value = true
    try {
      const { id } = tag
      const result = await api.put<Tag, AxiosResponse<Tag>, Tag>(
        `asset_tags/${id}`,
        tag,
      )
      const data = result.data
      updateStoreListItem<Tag>(list.value, data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: string): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete(`asset_tags/${id}`)
      removeStoreListItem<Tag>(list.value, id)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false
    list.value = []
  }

  return {
    initFlag,
    trigger,

    loading,
    loadingAction,

    getList,

    fetch,

    store,
    update,
    destroy,

    cancel,
    clear,
  }
})
